export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; allTools: string };
  hero: { title: string; subtitle: string; localBadge: string };
  ui: {
    type: string;
    typeText: string;
    typeVcard: string;
    typeWifi: string;
    typeEmail: string;
    typeTel: string;
    typeSms: string;
    contentLabel: string;
    contentPlaceholder: string;
    vcFirst: string;
    vcLast: string;
    vcOrg: string;
    vcTitle: string;
    vcPhone: string;
    vcEmail: string;
    vcUrl: string;
    vcSocial: string;
    vcSocialPlaceholder: string;
    wifiSsid: string;
    wifiPassword: string;
    wifiEncryption: string;
    wifiHidden: string;
    encNone: string;
    emailTo: string;
    emailSubject: string;
    emailBody: string;
    telNumber: string;
    smsNumber: string;
    smsMessage: string;
    style: string;
    foreground: string;
    background: string;
    dotStyle: string;
    dotSquare: string;
    dotRounded: string;
    dotDots: string;
    dotExtra: string;
    errorLevel: string;
    size: string;
    logo: string;
    logoDrop: string;
    logoRemove: string;
    logoHint: string;
    empty: string;
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', allTools: 'Alle Tools' },
  hero: {
    title: 'QR-Code-Generator',
    subtitle:
      'Erzeuge QR-Codes für URLs, vCards, WLAN und mehr — mit Logo, Farben und Stilen, direkt im Browser. Nichts wird gesendet oder gespeichert.',
    localBadge: '100 % lokal · kostenlos',
  },
  ui: {
    type: 'Typ',
    typeText: 'Text / URL',
    typeVcard: 'vCard (Kontakt)',
    typeWifi: 'WLAN',
    typeEmail: 'E-Mail',
    typeTel: 'Telefon',
    typeSms: 'SMS',
    contentLabel: 'Inhalt',
    contentPlaceholder: 'URL oder Text…',
    vcFirst: 'Vorname',
    vcLast: 'Nachname',
    vcOrg: 'Firma',
    vcTitle: 'Position',
    vcPhone: 'Telefon',
    vcEmail: 'E-Mail',
    vcUrl: 'Website',
    vcSocial: 'Social-Profile',
    vcSocialPlaceholder: '@handle oder URL',
    wifiSsid: 'Netzwerkname (SSID)',
    wifiPassword: 'Passwort',
    wifiEncryption: 'Verschlüsselung',
    wifiHidden: 'Verstecktes Netzwerk',
    encNone: 'keine',
    emailTo: 'Empfänger',
    emailSubject: 'Betreff',
    emailBody: 'Text',
    telNumber: 'Telefonnummer',
    smsNumber: 'Telefonnummer',
    smsMessage: 'Nachricht',
    style: 'Darstellung',
    foreground: 'Vordergrund',
    background: 'Hintergrund',
    dotStyle: 'Punkt-Stil',
    dotSquare: 'Eckig',
    dotRounded: 'Abgerundet',
    dotDots: 'Punkte',
    dotExtra: 'Extra rund',
    errorLevel: 'Fehlerkorrektur',
    size: 'Größe',
    logo: 'Logo',
    logoDrop: 'Logo hierher ziehen oder klicken',
    logoRemove: 'Logo entfernen',
    logoHint: 'Mit Logo wird automatisch die höchste Fehlerkorrektur (H) genutzt.',
    empty: 'Fülle die Felder aus, um einen QR-Code zu erzeugen.',
  },
  seo: {
    aboutTitle: 'QR-Codes sicher und lokal erzeugen',
    aboutBody:
      'Dieser Generator erzeugt QR-Codes (URL, vCard, WLAN, E-Mail, Telefon, SMS) inklusive Logo und Farben ausschließlich lokal in deinem Browser. Dein Inhalt und dein Logo werden nicht hochgeladen oder gespeichert.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Kann ich eine vCard (Kontakt) als QR erzeugen?',
        a: 'Ja. Wähle den Typ „vCard", fülle die Felder aus, und der QR-Code enthält die Kontaktdaten zum Direkt-Import.',
      },
      {
        q: 'Bleibt der QR-Code mit Logo scanbar?',
        a: 'Ja. Bei eingebettetem Logo nutzen wir automatisch die höchste Fehlerkorrektur (H), sodass genug Redundanz bleibt.',
      },
      {
        q: 'Wird mein Logo hochgeladen?',
        a: 'Nein. Das Logo wird lokal im Browser gelesen und eingebettet; es findet keine Übertragung statt.',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Alles läuft lokal im Browser.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', allTools: 'All tools' },
  hero: {
    title: 'QR Code Generator',
    subtitle:
      'Generate QR codes for URLs, vCards, Wi-Fi and more — with logo, colors and styles, right in your browser. Nothing is sent or stored.',
    localBadge: '100% local · free',
  },
  ui: {
    type: 'Type',
    typeText: 'Text / URL',
    typeVcard: 'vCard (contact)',
    typeWifi: 'Wi-Fi',
    typeEmail: 'Email',
    typeTel: 'Phone',
    typeSms: 'SMS',
    contentLabel: 'Content',
    contentPlaceholder: 'URL or text…',
    vcFirst: 'First name',
    vcLast: 'Last name',
    vcOrg: 'Company',
    vcTitle: 'Job title',
    vcPhone: 'Phone',
    vcEmail: 'Email',
    vcUrl: 'Website',
    vcSocial: 'Social profiles',
    vcSocialPlaceholder: '@handle or URL',
    wifiSsid: 'Network name (SSID)',
    wifiPassword: 'Password',
    wifiEncryption: 'Encryption',
    wifiHidden: 'Hidden network',
    encNone: 'none',
    emailTo: 'Recipient',
    emailSubject: 'Subject',
    emailBody: 'Body',
    telNumber: 'Phone number',
    smsNumber: 'Phone number',
    smsMessage: 'Message',
    style: 'Appearance',
    foreground: 'Foreground',
    background: 'Background',
    dotStyle: 'Dot style',
    dotSquare: 'Square',
    dotRounded: 'Rounded',
    dotDots: 'Dots',
    dotExtra: 'Extra rounded',
    errorLevel: 'Error correction',
    size: 'Size',
    logo: 'Logo',
    logoDrop: 'Drop a logo here or click',
    logoRemove: 'Remove logo',
    logoHint: 'With a logo the highest error correction (H) is used automatically.',
    empty: 'Fill in the fields to generate a QR code.',
  },
  seo: {
    aboutTitle: 'Generate QR codes securely and locally',
    aboutBody:
      'This generator creates QR codes (URL, vCard, Wi-Fi, email, phone, SMS) including logo and colors exclusively in your browser. Your content and logo are not uploaded or stored.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Can I generate a vCard (contact) QR?',
        a: 'Yes. Choose the "vCard" type, fill in the fields, and the QR code carries the contact details for direct import.',
      },
      {
        q: 'Does the QR stay scannable with a logo?',
        a: 'Yes. With an embedded logo we automatically use the highest error correction (H) to keep enough redundancy.',
      },
      {
        q: 'Is my logo uploaded?',
        a: 'No. The logo is read locally in the browser and embedded; nothing is transmitted.',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Everything runs locally in your browser.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
